import { Container, Graphics, Text, TextStyle } from '../../../vendor/pixi.min.mjs';
import { FROGGER_CONFIG } from './froggerConfig.js';

const {
    CELL_SIZE, COLS,
    ABILITY_COLORS, ABILITY_RADIUS, ABILITY_TYPES,
} = FROGGER_CONFIG;

const LABEL_STYLE = new TextStyle({
    fontFamily: 'Arial', fontSize: 10, fontWeight: 'bold', fill: '#ffffff',
});

const LABELS = { dash: 'D', slowtime: 'S', bomb: 'B' };

/** Dash icon: arrow asset is drawn facing right; rotate 90° CCW so it points up. */
const DASH_ROTATION_RAD = -Math.PI / 2;
/** Slow up-down bob for the dash icon (pixels of travel + radians per ms). */
const DASH_BOB_AMP = 6;
const DASH_BOB_RATE = 0.0025;

/**
 * Bomb-pickup fuse-spark tuning. While a bomb pickup is on the board we
 * emit a single tiny spark every `BOMB_SPARK_INTERVAL_MS` milliseconds
 * just above the icon's centre, so the dynamite reads as a lit, sputtering
 * fuse before the player runs over it.
 */
const BOMB_SPARK_INTERVAL_MS = 80;
/** Y offset (in px, NEGATIVE = above centre) of the fuse tip on the icon. */
const BOMB_SPARK_Y_OFFSET = -CELL_SIZE * 0.32;
/** Horizontal jitter around the fuse tip so consecutive sparks don't stack. */
const BOMB_SPARK_X_JITTER = CELL_SIZE * 0.06;

/**
 * Manages collectible ability pickups on road rows.
 * Walk over one to activate immediately.
 */
export class FroggerAbilities {
    container = new Container();

    /** @type {Map<string, { type: string, worldRow: number, gridCol: number, gfx: Container }>} */
    _items = new Map();

    _originX = 0;
    _themeManager = null;

    /** @type {{ burst: (x: number, y: number, kind: string) => void }|null} */
    _burstEmitter = null;

    constructor(originX, themeManager = null) {
        this._originX = originX;
        this._themeManager = themeManager;
    }

    /**
     * Hand the abilities system a confetti emitter (e.g. `FroggerPickupBurst`).
     * Bomb pickups use it to sputter fuse sparks while idle on the board.
     */
    setBurstEmitter(emitter) {
        this._burstEmitter = emitter || null;
    }

    /**
     * Place an ability pickup on a road row.
     * @param {number} worldRow
     * @param {number} gridCol
     * @param {string} [type] — one of ABILITY_TYPES; random if omitted
     */
    addAbility(worldRow, gridCol, type) {
        const key = `${gridCol},${worldRow}`;
        if (this._items.has(key)) return;
        if (!type) type = ABILITY_TYPES[Math.floor(Math.random() * ABILITY_TYPES.length)];

        const gfx = this._buildDisplay(type);
        gfx.x = this._originX + gridCol * CELL_SIZE + CELL_SIZE / 2;
        gfx.y = -worldRow * CELL_SIZE + CELL_SIZE / 2;
        this.container.addChild(gfx);

        this._items.set(key, {
            type, worldRow, gridCol, gfx,
            // Stagger the first spark per bomb so multiple icons on screen
            // don't sputter in perfect lockstep.
            lastSparkAt: type === 'bomb' ? Date.now() - Math.random() * BOMB_SPARK_INTERVAL_MS : 0,
        });
    }

    /**
     * Check if the player is on an ability pickup.
     * @returns {{ type: string } | null}
     */
    checkCollection(gridCol, worldRow) {
        const key = `${gridCol},${worldRow}`;
        const item = this._items.get(key);
        if (!item) return null;

        this.container.removeChild(item.gfx);
        item.gfx.destroy();
        this._items.delete(key);

        return { type: item.type };
    }

    pulse(playerRow) {
        const now = Date.now();
        for (const [, item] of this._items) {
            item.gfx.alpha = 1;
            if (item.type === 'dash') {
                const sprite = item.gfx.iconSprite;
                if (sprite) sprite.y = Math.sin(now * DASH_BOB_RATE) * DASH_BOB_AMP;
                item.gfx.scale.set(1);
            } else {
                item.gfx.scale.set(0.96 + 0.06 * Math.sin(now * 0.005));
            }

            // Sputter a fuse spark above each bomb pickup. Throttled by
            // wall-clock so the rate is independent of frame rate.
            if (item.type === 'bomb' && this._burstEmitter
                && now - item.lastSparkAt >= BOMB_SPARK_INTERVAL_MS) {
                item.lastSparkAt = now;
                const jx = (Math.random() - 0.5) * BOMB_SPARK_X_JITTER * 2;
                this._burstEmitter.burst(
                    item.gfx.x + jx,
                    item.gfx.y + BOMB_SPARK_Y_OFFSET,
                    'fuseSpark'
                );
            }
        }
    }

    cleanupOutside(lowRow, highRow) {
        for (const [key, item] of this._items) {
            if (item.worldRow < lowRow || item.worldRow > highRow) {
                this.container.removeChild(item.gfx);
                item.gfx.destroy();
                this._items.delete(key);
            }
        }
    }

    destroy() {
        for (const [, item] of this._items) {
            item.gfx.destroy();
        }
        this._items.clear();
        this.container.removeChildren();
    }

    _buildDisplay(type) {
        const slotMap = {
            dash: 'frogger.ability.dash',
            slowtime: 'frogger.ability.slow',
            bomb: 'frogger.ability.bomb',
        };
        const size = CELL_SIZE * 0.9;
        const sprite = this._themeManager?.createSprite(
            slotMap[type],
            size, size,
            { fitMode: 'contain' }
        );
        if (sprite) {
            const wrap = new Container();
            if (type === 'dash') sprite.rotation = DASH_ROTATION_RAD;
            wrap.addChild(sprite);
            wrap.iconSprite = sprite;
            return wrap;
        }

        const color = ABILITY_COLORS[type] ?? 0xffffff;
        const g = new Graphics();

        if (type === 'dash') {
            g.moveTo(-ABILITY_RADIUS, ABILITY_RADIUS * 0.5);
            g.lineTo(ABILITY_RADIUS, 0);
            g.lineTo(-ABILITY_RADIUS, -ABILITY_RADIUS * 0.5);
            g.closePath();
            g.fill(color);
        } else if (type === 'slowtime') {
            g.circle(0, 0, ABILITY_RADIUS);
            g.fill(color);
            g.moveTo(0, -ABILITY_RADIUS * 0.6);
            g.lineTo(0, 0);
            g.lineTo(ABILITY_RADIUS * 0.4, ABILITY_RADIUS * 0.3);
            g.stroke({ color: 0xffffff, width: 2 });
        } else {
            g.circle(0, 0, ABILITY_RADIUS);
            g.fill(color);
            g.moveTo(-ABILITY_RADIUS * 0.3, -ABILITY_RADIUS * 0.3);
            g.lineTo(ABILITY_RADIUS * 0.3, ABILITY_RADIUS * 0.3);
            g.moveTo(ABILITY_RADIUS * 0.3, -ABILITY_RADIUS * 0.3);
            g.lineTo(-ABILITY_RADIUS * 0.3, ABILITY_RADIUS * 0.3);
            g.stroke({ color: 0xffffff, width: 2 });
        }

        const label = new Text({ text: LABELS[type] || '?', style: LABEL_STYLE });
        label.anchor.set(0.5);
        label.y = ABILITY_RADIUS + 8;
        g.addChild(label);

        return g;
    }
}
